import { FieldValidationMetaInfo, localize, setLocale } from '@vee-validate/i18n';
import en from '@vee-validate/i18n/dist/locale/en.json';
import fr from '@vee-validate/i18n/dist/locale/fr.json';
import { digits, email, length, max, min, numeric, regex, required, url } from '@vee-validate/rules';
import { DateTime } from 'luxon';
import { configure, defineRule } from 'vee-validate';

import { Locale } from '@silae/composables/i18n';
import { ISODateString } from '@silae/helpers/types';
import { i18n } from '~/plugins/vue-i18n/vue-i18n.plugin';
import { DEFAULT_LOCALE } from '~/utils';

type ValidationGenerateMessage = (ctx: FieldValidationMetaInfo) => string;

export const updateVeeValidateConfig = (locale: Locale = DEFAULT_LOCALE) => {
	setLocale(locale.value);
};

export const setupVeeValidate = (config: Locale = DEFAULT_LOCALE) => {
	/* import rules from vee validate global validators, see: https://vee-validate.logaretm.com/v4/guide/global-validators/#vee-validaterules */
	/* to import more rules : https://vee-validate.logaretm.com/v4/guide/global-validators/#available-rules */
	defineRule('email', email);
	defineRule('digits', digits);
	defineRule('required', required);
	defineRule('min', min);
	defineRule('max', max);
	defineRule('url', url);
	defineRule('regex', regex);
	defineRule('numeric', numeric);
	defineRule('length', length);

	/* localization for error messages, see: https://vee-validate.logaretm.com/v4/guide/i18n/ */
	/* french locale messages can be found here : https://www.npmjs.com/package/@vee-validate/i18n?activeTab=code */
	const simpleErrorMessageGenerator = localize({
		en: {
			...en,
			messages: {
				...en.messages,
				required: 'This field is required'
			}
		},
		fr: {
			...fr,
			messages: {
				...fr.messages,
				required: 'Ce champ est requis'
			}
		}
	});

	const complexErrorMessageGenerator: ValidationGenerateMessage = (ctx: FieldValidationMetaInfo) => {
		const { t } = i18n.global;

		if (ctx.rule?.name === 'before' || ctx.rule?.name === 'beforeOffset') {
			const max: ISODateString = (ctx.rule?.params as any)?.[0];
			const daysOffset: number = (ctx.rule?.params as any)?.[1] ?? 0;

			if (max != null) {
				const maxDate = DateTime.fromISO(max).plus({ days: daysOffset }).toLocaleString(DateTime.DATE_SHORT);
				return t('common.rules.before', { date: maxDate });
			}
		}

		if (ctx.rule?.name === 'after') {
			const max: ISODateString = (ctx.rule?.params as any)?.[0];
			if (max != null) {
				const maxDate = DateTime.fromISO(max).toLocaleString(DateTime.DATE_SHORT);
				return t('common.rules.after', { date: maxDate });
			}
		}

		const ruleKey = `common.rules.${ctx.rule?.name}`;

		// Make sure we have an object (not an array) with the parameters
		const ruleParams = Array.isArray(ctx.rule?.params) ? {} : ctx.rule?.params || {};

		// Return the translation if it exists, or use the default message generator
		const translation = t(ruleKey, ruleParams);
		return translation === ruleKey ? simpleErrorMessageGenerator(ctx) : translation;
	};

	configure({
		generateMessage: complexErrorMessageGenerator
	});
	updateVeeValidateConfig(config);
};
